import classNames from 'classnames';
import logo from 'assets/img/logo.svg';
import smallLogo from 'assets/img/smallLogo.svg';
import useAuthStore from 'store/authStore';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Pages } from 'utils/pages.utils';
import { useMsal } from '@azure/msal-react';
import { BizzIcon } from 'assets/icons';
import useSettingsStore from 'store/settingsStore';
import {
  AccessLevel,
  haveAccess,
  haveRole,
  PAGES_ENUM,
  USER_ROLES_ENUM,
} from 'utils/authenticationUtils';
import React from 'react';

import {
  BanknoteXIcon,
  UserCogIcon,
  CoinsIcon,
  ListIcon,
  ListVideoIcon,
  ListXIcon,
  ListOrderedIcon,
  ListTodoIcon,
  LogOutIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  ReceiptEuroIcon,
  ReceiptTextIcon,
  ListChecksIcon,
  ChevronDownIcon,
  ArrowLeftRightIcon,
} from 'lucide-react';

interface MenuItemProps {
  link: string;
  icon: React.ReactElement;
  label: string;
  access?: boolean;
}

function MenuItem({ link, icon, label, access = false }: MenuItemProps) {
  const { sideMenuOpen } = useSettingsStore();
  if (!access) {
    // No access
    return null;
  }
  return (
    <li className="my-2" role="button">
      <NavLink
        className={({ isActive }) =>
          classNames(
            'flex min-h-10 flex-row items-center gap-2 rounded-md py-2',
            sideMenuOpen && 'px-3',
            !sideMenuOpen && 'pl-3',
            !isActive && 'text-primary hover:bg-neutral-200',
            isActive && 'active bg-green-600 font-semibold text-white'
          )
        }
        to={link}
        title={label}
      >
        {icon} {sideMenuOpen && label}
      </NavLink>
    </li>
  );
}

interface SubMenuItemProps {
  link: string;
  icon: React.ReactElement;
  label: string;
  access?: boolean;
}

function SubMenuItem({ link, icon, label, access = false }: SubMenuItemProps) {
  const { sideMenuOpen } = useSettingsStore();
  if (!access) {
    // No access
    return null;
  }
  return (
    <li role="button">
      <NavLink
        className={({ isActive }) =>
          classNames(
            'flex min-h-10 flex-row items-center gap-2 rounded-md',
            sideMenuOpen && 'px-2 py-2',
            !sideMenuOpen && 'pl-3',
            !isActive && 'text-primary hover:bg-neutral-200',
            isActive && 'active bg-green-600 font-semibold text-white'
          )
        }
        to={link}
        title={label}
      >
        {icon} {sideMenuOpen && label}
      </NavLink>
    </li>
  );
}

interface SubMenuProps extends React.PropsWithChildren {
  link: string;
  icon: React.ReactElement;
  label: string;
  access?: boolean;
}

function SubMenu({ link, icon, label, access = false, children }: SubMenuProps) {
  const [openForPath, setOpenForPath] = React.useState<string | null>(null);
  const location = useLocation();
  const { sideMenuOpen } = useSettingsStore();

  const [prevPathname, setPrevPathname] = React.useState(location.pathname);
  if (prevPathname !== location.pathname) {
    setPrevPathname(location.pathname);
    setOpenForPath(null);
  }

  const open = openForPath === location.pathname;

  if (!access) {
    // No access
    return null;
  }
  const isMatchingPath = window.location.pathname.startsWith(link);

  return (
    <li
      className={classNames(
        'my-2',
        // window.location.pathname.startsWith(Pages.obeParent) &&
        (open || isMatchingPath) &&
          'outline- rounded-sm outline-2 outline-offset-2 outline-green-200 outline-double',
        ''
      )}
      role="button"
    >
      <div
        className={classNames(
          'flex min-h-10 flex-row items-center rounded-md py-2',
          sideMenuOpen && 'px-3',
          !sideMenuOpen && 'pl-3',
          !isMatchingPath && !open && 'text-primary hover:bg-neutral-200',
          !isMatchingPath && open && 'active bg-neutral-200 font-semibold',
          isMatchingPath && 'active text-primary bg-neutral-200 font-semibold',
          (isMatchingPath || open) && 'rounded-b-none'
        )}
        onClick={() => {
          if (!isMatchingPath) {
            setOpenForPath(open ? null : location.pathname);
          }
        }}
      >
        <div className="flex w-full flex-row items-center justify-between">
          <div className="flex w-full flex-row items-center justify-between gap-2">
            <div className="flex flex-row items-center justify-between gap-2">
              {icon} {sideMenuOpen && label}
            </div>
            <ChevronDownIcon className={classNames('transition-all', open && 'rotate-180')} />
          </div>
          {/* <div>{open ? <MinusCircleIcon size={20} /> : <PlusCircleIcon size={20} />}</div> */}
        </div>
      </div>
      {(isMatchingPath || open) && (
        <ul
          data-type="submenu"
          className={classNames(
            'flex flex-col gap-2 rounded-b-md border border-neutral-200 bg-white py-1',
            sideMenuOpen && 'px-1'
          )}
        >
          {children}
        </ul>
      )}
    </li>
  );
}

interface SideMenuProps {
  className?: string;
}

function SideMenu({ className }: SideMenuProps) {
  const navigate = useNavigate();
  const { sideMenuOpen, setSideMenuOpen } = useSettingsStore();

  const { clearToken } = useAuthStore();
  const { instance } = useMsal();

  return (
    <div
      className={classNames(
        'relative flex flex-col border-r border-neutral-100 bg-neutral-50',
        !sideMenuOpen && 'w-24',
        sideMenuOpen && 'w-80',
        className
      )}
    >
      <div
        className={classNames(
          '_overflow-y-auto fixed h-screen overflow-y-scroll',
          !sideMenuOpen && 'w-24',
          sideMenuOpen && 'w-80'
        )}
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className={classNames('relative w-full', !sideMenuOpen && '', sideMenuOpen && 'pt-5')}>
          <div className={classNames('px-6 pb-6')}>
            {sideMenuOpen && (
              <>
                <div className="pt-6">
                  <Link to="/">
                    <img className="pb-5" src={logo} alt="ØsbPAY logo" />
                  </Link>
                </div>
                <div className="pb-2 text-3xl font-semibold text-neutral-900">Tolling Platform</div>
              </>
            )}
            {!sideMenuOpen && (
              <div className="pt-5">
                <img src={smallLogo} alt="Small ØsbPAY logo" />
              </div>
            )}
            <ul className="relative pt-1">
              <MenuItem
                link={Pages.billingDetailsList}
                label="Billing Details"
                icon={<ReceiptEuroIcon size={20} />}
                access={haveAccess(PAGES_ENUM.billingDetails, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.transactionsList}
                label="Transactions"
                icon={<ArrowLeftRightIcon size={20} />}
                access={haveAccess(PAGES_ENUM.transactions, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.agreementLineList}
                label="Agreement Lines"
                icon={<ListChecksIcon size={20} />}
                access={haveAccess(PAGES_ENUM.agreementLines, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.tollDeclarationsList}
                icon={<ReceiptTextIcon size={20} />}
                label="Toll Declarations"
                access={haveAccess(PAGES_ENUM.tollDeclarations, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.paymentClaimsList}
                icon={<CoinsIcon size={20} />}
                label="Payment Claims"
                access={haveAccess(PAGES_ENUM.paymentClaims, AccessLevel.VIEW)}
              />
              <MenuItem
                link={Pages.auditLog}
                label="Audit Log"
                icon={<ListXIcon size={20} />}
                access={haveAccess(PAGES_ENUM.auditLog, AccessLevel.VIEW)}
              />

              <SubMenu
                link={Pages.obeParent}
                icon={<BizzIcon size={20} />}
                label="OBE"
                access={
                  haveAccess(PAGES_ENUM.obeEventLog, AccessLevel.VIEW) ||
                  haveAccess(PAGES_ENUM.assetManagementList, AccessLevel.VIEW)
                }
              >
                <SubMenuItem
                  link={Pages.obeEventLog}
                  label="Event Log (all - 30d)"
                  icon={<ListVideoIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.obeEventLog, AccessLevel.VIEW)}
                />
                <SubMenuItem
                  link={Pages.obeKapschEventLog}
                  label="Event Log (single - 180d)"
                  icon={<ListVideoIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.obeEventLog, AccessLevel.VIEW)}
                />
                <SubMenuItem
                  link={Pages.obeFineHelper}
                  label="Fine helper"
                  icon={<BanknoteXIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.obeEventLog, AccessLevel.VIEW)}
                />
                {/* <SubMenuItem
                  link={Pages.obeList}
                  label="Registry"
                  icon={<BizzIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.obeRegistry, AccessLevel.VIEW)}
                /> */}
                <SubMenuItem
                  link={Pages.assetManagementList}
                  label="Asset Management"
                  icon={<ListTodoIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.assetManagementList, AccessLevel.VIEW)}
                />
              </SubMenu>

              <MenuItem
                link={Pages.exceptionLists}
                label="Exception Lists"
                icon={<ListTodoIcon size={20} />}
                access={haveAccess(PAGES_ENUM.exceptionLists, AccessLevel.VIEW)}
              />

              <SubMenu
                link={Pages.efcContextData}
                icon={<BizzIcon size={20} />}
                label="EFC Context Data"
                access={haveAccess(PAGES_ENUM.efcContextData, AccessLevel.VIEW)}
              >
                <SubMenuItem
                  link={Pages.efcContextDataInbound}
                  label="Inbound"
                  icon={<ListVideoIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.efcContextData, AccessLevel.VIEW)}
                />
                <SubMenuItem
                  link={Pages.efcContextDataOutbound}
                  label="Outbound"
                  icon={<ListVideoIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.efcContextData, AccessLevel.VIEW)}
                />
              </SubMenu>

              <SubMenu
                link={Pages.statusAduList}
                icon={<BizzIcon size={20} />}
                label="Status ADU"
                access={haveAccess(PAGES_ENUM.inboundStatusAdu, AccessLevel.VIEW)}
              >
                <SubMenuItem
                  link={Pages.statusAduListInbound}
                  label="Inbound"
                  icon={<ListVideoIcon size={20} />}
                  access={haveAccess(PAGES_ENUM.inboundStatusAdu, AccessLevel.VIEW)}
                />
              </SubMenu>

              <MenuItem
                link={Pages.tollChargersList}
                icon={<ListOrderedIcon size={20} />}
                label="Toll Chargers Data"
                access={haveAccess(PAGES_ENUM.tollChargers, AccessLevel.VIEW)}
              />

              <MenuItem
                link={Pages.oresundPayData}
                icon={<ListOrderedIcon size={20} />}
                label="ØresundPAY  Data"
                access={haveAccess(PAGES_ENUM.oresundPayData, AccessLevel.VIEW)}
              />

              <MenuItem
                link={Pages.efcContextMarksList}
                label="EFC Context Marks"
                icon={<ListIcon size={20} />}
                access={haveAccess(PAGES_ENUM.efcContextMarks, AccessLevel.VIEW)}
              />

              <MenuItem
                link={Pages.contractIssuerList}
                icon={<ListOrderedIcon size={20} />}
                label="Contract Issuer Lists"
                access={haveAccess(PAGES_ENUM.contractIssuerList, AccessLevel.VIEW)}
              />

              <MenuItem
                link={Pages.abnormalBehaviorReportList}
                icon={<ListOrderedIcon size={20} />}
                label="Abnormal Behavior Reports"
                // access={haveAccess(PAGES_ENUM.abnormalBehaviorReports, AccessLevel.VIEW)}
                access={haveAccess(PAGES_ENUM.oresundPayData, AccessLevel.VIEW)}
              />

              <MenuItem
                link={Pages.adminPage}
                label="Admin"
                icon={<UserCogIcon size={20} />}
                access={haveRole(USER_ROLES_ENUM.userAdminGroup)}
              />
            </ul>
            <hr />
            <div
              className="my-2 flex min-h-10 flex-row items-center rounded-md px-3 py-2 hover:bg-neutral-200"
              role="button"
              onClick={() => {
                void (async () => {
                  try {
                    const response = await instance.logoutPopup();
                    clearToken();
                    console.log('Logout response', response);
                    navigate('/');
                  } catch (error) {
                    console.log('Logout error', error);
                  }
                })();
              }}
              title="Log out"
            >
              <span className="pr-5">
                <LogOutIcon size={20} />
              </span>
              {sideMenuOpen && <> Log out</>}
            </div>
            <hr />
            <div
              className="my-2 flex min-h-10 flex-row items-center rounded-md px-3 py-2 hover:bg-neutral-200"
              role="button"
              onClick={() => setSideMenuOpen(!sideMenuOpen)}
              title={sideMenuOpen ? 'Minimize menu' : 'Maximize menu'}
            >
              <span className="pr-5">
                {sideMenuOpen ? <PanelLeftCloseIcon size={20} /> : <PanelLeftOpenIcon size={20} />}
              </span>
              {sideMenuOpen && <> Minimize menu</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SideMenu;
